import React, { useState, useEffect } from "react";
import "../Settings/ChangePassword.css";
import { toast } from "react-toastify";
import { PYTHON_API_BASE_URL } from "../../services/api/apiConstants";
import { emailQuery } from "../../features/users/domain/emailQuery";

export default function UserChangePassword() {
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [userPayload, setUserPayload] = useState(null);
  const [errors, setErrors] = useState({});
  const [apiErrors, setApiErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [showPasswords, setShowPasswords] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [saving, setSaving] = useState(false);
  const employeeEmail = "demoemployee@gmail.com";

  const fetchUrl = `${PYTHON_API_BASE_URL}/users/?${emailQuery(employeeEmail)}`;
  const putUrl = `${PYTHON_API_BASE_URL}/users/update_from/?${emailQuery(employeeEmail)}`;
  // Fetch the full payload
  const fetchUserDetails = async () => {
    try {
      const response = await fetch(fetchUrl, {
        headers: {
          "X-User-ID": sessionStorage.getItem("UserID") || "",
        },
      });
      if (!response.ok) {
        throw new Error(`Error fetching user details: ${response.statusText}`);
      }
      const data = await response.json();
      if (data.length > 0) {
        setUserPayload(data[0]);
        setPasswordData((prevState) => ({
          ...prevState,
          currentPassword: data[0].password,
        }));
      }
    } catch (error) {
      toast.error("Error fetching user details:", error);
    }
  };

  // Call fetchUserDetails on component mount
  useEffect(() => {
    fetchUserDetails();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({
      ...passwordData,
      [name]: value,
    });
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  const validatePassword = () => {
    const newErrors = {};
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#_])[A-Za-z\d@$!%*?&#_]{8,}$/;

    if (!passwordData.currentPassword.trim()) {
      newErrors.currentPassword = "Current password is required";
    }

    if (!passwordData.newPassword.trim()) {
      newErrors.newPassword = "New password is required";
    } else if (!passwordRegex.test(passwordData.newPassword)) {
      newErrors.newPassword =
        "Password must be at least 8 characters long, include uppercase, lowercase, numbers, and special characters and not include spaces";
    } else if (passwordData.newPassword === passwordData.currentPassword) {
      newErrors.newPassword =
        "New password cannot be the same as the current password";
    }

    if (!passwordData.confirmPassword.trim()) {
      newErrors.confirmPassword = "Confirm password is required";
    } else if (passwordData.confirmPassword !== passwordData.newPassword) {
      newErrors.confirmPassword = "Does not match the new password";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validatePassword() && userPayload) {
      setSaving(true);
       let timeoutId; 
      try {
        const updatedPayload = {
          ...userPayload,
          password: passwordData.newPassword,
        };

        const response = await fetch(putUrl, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "X-User-ID": sessionStorage.getItem("UserID") || "",
          },
          body: JSON.stringify(updatedPayload),
        });

        if (!response.ok) {
          const errorData = await response.json();
          setApiErrors(errorData);
          throw new Error(`Failed to update: ${response.statusText}`);
        }

        setSuccessMessage("Password has been updated successfully!");
        timeoutId = setTimeout(() => setSuccessMessage(""), 3000);

        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });

        fetchUserDetails();
      } catch (error) {
        toast.error("Error updating password:", error);
        toast.error("An error occurred while updating the password.");
      } finally {
        setSaving(false);
      }
       return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
    }
  };

  return (
    <div>
      <h2>Change Password</h2>

      {/* Success message */}
      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="currentPassword">Current Password</label>
              <div className="password-wrapper">
                <input
                  type={showPasswords.currentPassword ? "text" : "password"}
                  className={`form-control custom-password-input`}
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  maxLength={50}
                  onChange={handleInputChange}
                  autoComplete="off"
                  disabled={true}
                />

                <i
                  className={`fas ${
                    showPasswords.currentPassword ? "fa-eye-slash" : "fa-eye"
                  } account-password-icon-change`}
                  onClick={() => togglePasswordVisibility("currentPassword")}
                ></i>
              </div>

              {errors.currentPassword && (
                <span className="error-message">{errors.currentPassword}</span>
              )}
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <div className="password-wrapper">
                <input
                  type={showPasswords.newPassword ? "text" : "password"}
                  className={`form-control custom-password-input`}
                  name="newPassword"
                  maxLength={50}
                  value={passwordData.newPassword}
                  onChange={handleInputChange}
                  disabled={saving}
                />
                <i
                  className={`fas ${
                    showPasswords.newPassword ? "fa-eye-slash" : "fa-eye"
                  } account-password-icon-change`}
                  onClick={() => togglePasswordVisibility("newPassword")}
                ></i>
              </div>
              {errors.newPassword && (
                <span className="error-message">{errors.newPassword}</span>
              )}
            </div>
          </div>

          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="password-wrapper">
                <input
                  type={showPasswords.confirmPassword ? "text" : "password"}
                  className={`form-control custom-password-input`}
                  name="confirmPassword"
                  maxLength={50}
                  value={passwordData.confirmPassword}
                  onChange={handleInputChange}
                  disabled={saving}
                />
                <i
                  className={`fas ${
                    showPasswords.confirmPassword ? "fa-eye-slash" : "fa-eye"
                  } account-password-icon-change`}
                  onClick={() => togglePasswordVisibility("confirmPassword")}
                ></i>
              </div>
              {errors.confirmPassword && (
                <span className="error-message">{errors.confirmPassword}</span>
              )}
            </div>
          </div>

          <div className="col-md-12">
            <div className="button-container d-flex justify-content-end">
              <button type="submit" className="btn-edit" disabled={saving}>
                Update
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
